import { OpenRouterConfig } from '../components/OpenRouterConfig';
import { Language } from '../hooks/useLanguage';

export interface PromptEvaluation {
  score: number;
  suggestions: PromptSuggestion[];
  analysis: PromptAnalysis;
  response: string;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface PromptSuggestion {
  type: 'clarity' | 'specificity' | 'structure' | 'context' | 'examples' | 'constraints';
  severity: 'low' | 'medium' | 'high';
  title: string;
  description: string;
  example?: string;
  reasoning: string;
}

export interface PromptAnalysis {
  clarity: number;
  specificity: number;
  structure: number;
  context: number;
  examples: boolean;
  constraints: boolean;
  wordCount: number;
  tokenEstimate: number;
  readabilityScore: number;
  overallScore: number;
}

const getEvaluationSystemPrompt = (language: Language) => {
  const languageInstruction = language === 'pt-BR' ? 
    'INSTRUÇÃO FUNDAMENTAL: Responda SEMPRE em português brasileiro.' : 
    'FUNDAMENTAL INSTRUCTION: ALWAYS respond in English.';

  return `You are an expert AI prompt engineer in the style of Andrej Karpathy.

${languageInstruction}

FUNDAMENTAL RULE: Analyze the language of the user's prompt and respond in the SAME language.
- If the prompt is in Portuguese, respond entirely in Portuguese (Brazilian Portuguese)
- If the prompt is in English, respond entirely in English
- Never mix languages in your response

Evaluate the prompt on these dimensions (0-100 scale):
- Clarity: How clear and unambiguous is the instruction?
- Specificity: How specific and detailed are the requirements?
- Structure: How well organized and formatted is the prompt?
- Context: How much relevant context is provided?
- Examples: Does it include helpful examples? (boolean)
- Constraints: Are there clear constraints and boundaries? (boolean)

Provide 3-5 specific suggestions for improvement, categorized by:
- clarity: Making instructions clearer
- specificity: Adding more specific details
- structure: Improving organization and format
- context: Adding relevant background information
- examples: Including examples or demonstrations
- constraints: Setting clear boundaries and limitations

For each suggestion, specify severity (low/medium/high) and provide:
- A clear title (in the same language as the prompt)
- Detailed description of the issue (in the same language as the prompt)
- An example of how to fix it (in the same language as the prompt)
- Reasoning behind the suggestion (in the same language as the prompt)

IMPORTANT: Your final response must be ONLY a valid JSON, with no additional text before or after.

If you are a reasoning model (like DeepSeek R1):
- You can include your internal reasoning
- But your final JSON analysis must be in the SAME LANGUAGE as the user's prompt
- Do not mix languages in your final response

JSON response format:
{
  "analysis": {
    "clarity": number,
    "specificity": number,
    "structure": number,
    "context": number,
    "examples": boolean,
    "constraints": boolean,
    "overallScore": number
  },
  "suggestions": [
    {
      "type": "category",
      "severity": "level",
      "title": "title in same language as prompt",
      "description": "description in same language as prompt",
      "example": "example in same language as prompt",
      "reasoning": "reasoning in same language as prompt"
    }
  ]
}`;
};

export class OpenRouterService {
  private config: OpenRouterConfig;

  constructor(config: OpenRouterConfig) {
    this.config = config;
  }

  updateConfig(config: OpenRouterConfig) {
    this.config = config;
  }

  private detectPromptLanguage(prompt: string): Language {
    // Simple language detection based on common Portuguese words/patterns
    const portugueseIndicators = [
      // Common Portuguese words
      'você', 'para', 'com', 'por', 'uma', 'seu', 'sua', 'seus', 'suas', 'que', 'como', 'onde', 'quando',
      'escreva', 'crie', 'faça', 'desenvolva', 'explique', 'descreva', 'analise', 'conte',
      // Portuguese verbs
      'é', 'está', 'tem', 'pode', 'deve', 'precisa', 'quer', 'vai', 'será',
      // Portuguese specific characters and patterns
      'ção', 'são', 'não', 'até', 'através', 'então', 'também', 'além'
    ];

    const englishIndicators = [
      // Common English words
      'you', 'your', 'the', 'and', 'for', 'with', 'that', 'this', 'what', 'how', 'where', 'when',
      'write', 'create', 'make', 'develop', 'explain', 'describe', 'analyze', 'tell',
      // English verbs
      'is', 'are', 'has', 'have', 'can', 'should', 'must', 'need', 'want', 'will', 'would'
    ];

    const lowerPrompt = prompt.toLowerCase();
    
    let portugueseScore = 0;
    let englishScore = 0;

    // Count Portuguese indicators
    portugueseIndicators.forEach(indicator => {
      const matches = (lowerPrompt.match(new RegExp(`\\b${indicator}\\b`, 'g')) || []).length;
      portugueseScore += matches;
    });

    // Count English indicators
    englishIndicators.forEach(indicator => {
      const matches = (lowerPrompt.match(new RegExp(`\\b${indicator}\\b`, 'g')) || []).length;
      englishScore += matches;
    });

    // Additional checks for Portuguese specific patterns
    if (/ção\b/.test(lowerPrompt)) portugueseScore += 2;
    if (/ão\b/.test(lowerPrompt)) portugueseScore += 1;
    if (/ê|í|ú|ô|á|à|ã/.test(lowerPrompt)) portugueseScore += 1;

    // Return detected language or fallback to interface language
    if (portugueseScore > englishScore) {
      return 'pt-BR';
    } else if (englishScore > portugueseScore) {
      return 'en';
    } else {
      // If inconclusive, return 'pt-BR' as default since it's Brazilian app
      return 'pt-BR';
    }
  }

  private buildEvaluationUserPrompt(prompt: string, interfaceLanguage: Language): string {
    // Use interface language to determine response language, not prompt detection
    // This ensures consistency with user's language preference
    
    const instructions = {
      'pt-BR': `Analise este prompt e responda EXCLUSIVAMENTE em português brasileiro:

${prompt}

SEMPRE EM PORTUGUÊS: Toda sua análise deve ser em português brasileiro (títulos, descrições, exemplos, raciocínios). Nunca misture idiomas na resposta.`,

      'en': `Analyze this prompt and respond EXCLUSIVELY in English:

${prompt}

ALWAYS IN ENGLISH: Your entire analysis must be in English (titles, descriptions, examples, reasoning). Never mix languages in the response.`
    };

    return instructions[interfaceLanguage];
  }

  async evaluatePrompt(prompt: string, language: Language = 'pt-BR'): Promise<PromptEvaluation> {
    if (!this.config.apiKey) {
      throw new Error(language === 'pt-BR' ? 'API Key do OpenRouter não configurada' : 'OpenRouter API Key not configured');
    }

    if (!prompt.trim()) {
      throw new Error(language === 'pt-BR' ? 'Prompt não pode estar vazio' : 'Prompt cannot be empty');
    }

    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Prompt Editor - Aldeia Viva'
        },
        body: JSON.stringify({
          model: this.config.model,
          messages: [
            { role: 'system', content: getEvaluationSystemPrompt(language) },
            { role: 'user', content: this.buildEvaluationUserPrompt(prompt, language) }
          ],
          max_tokens: this.config.maxTokens,
          temperature: 0.3, // Lower temperature for more consistent analysis
          ...(this.config.model.includes('gpt-') || this.config.model.includes('claude-') ? 
            { response_format: { type: 'json_object' } } : 
            {})
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`OpenRouter API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      console.log('Evaluation response data:', data); // Debug log
      
      // Handle different response structures for evaluation
      let content = '';
      
      if (data.choices && data.choices.length > 0) {
        const choice = data.choices[0];
        
        // Standard content
        if (choice.message?.content) {
          content = choice.message.content;
        }
        // Some models might have different structure
        else if (choice.text) {
          content = choice.text;
        }
        // DeepSeek R1 and reasoning models - for JSON analysis, we want the final answer
        else if (choice.message?.answer) {
          content = choice.message.answer;
        }
        // If reasoning is available, try to extract JSON from it
        else if (choice.message?.reasoning) {
          const reasoning = choice.message.reasoning;
          // Try to find JSON in reasoning
          const jsonMatch = reasoning.match(/\{[\s\S]*\}/);
          content = jsonMatch ? jsonMatch[0] : reasoning;
        }
      }
      
      if (!content) {
        console.warn('No content found in evaluation response:', JSON.stringify(data, null, 2));
        throw new Error(language === 'pt-BR' ? 'Resposta vazia do OpenRouter' : 'Empty response from OpenRouter');
      }

      let analysisResult;
      try {
        // Try to parse as JSON first
        analysisResult = JSON.parse(content);
      } catch (error) {
        // If JSON parsing fails, try to extract JSON from the response
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            analysisResult = JSON.parse(jsonMatch[0]);
          } catch (secondError) {
            // If still fails, create a fallback response
            console.warn('Failed to parse JSON from model response, using fallback');
            analysisResult = this.createFallbackAnalysis(content, language);
          }
        } else {
          // No JSON found, create fallback
          console.warn('No JSON found in model response, using fallback');
          analysisResult = this.createFallbackAnalysis(content, language);
        }
      }

      // Calculate additional metrics
      const wordCount = prompt.split(/\s+/).filter(word => word.length > 0).length;
      const tokenEstimate = this.estimateTokens(prompt);
      const readabilityScore = this.calculateReadability(prompt);

      const analysis: PromptAnalysis = {
        ...analysisResult.analysis,
        wordCount,
        tokenEstimate,
        readabilityScore
      };

      const suggestions: PromptSuggestion[] = analysisResult.suggestions || [];

      // Generate test response to show what the prompt would produce
      const testResponse = await this.generateTestResponse(prompt);

      return {
        score: analysis.overallScore,
        suggestions,
        analysis,
        response: testResponse,
        usage: {
          promptTokens: data.usage?.prompt_tokens || 0,
          completionTokens: data.usage?.completion_tokens || 0,
          totalTokens: data.usage?.total_tokens || 0
        }
      };

    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(language === 'pt-BR' ? 'Erro desconhecido durante avaliação' : 'Unknown error during evaluation');
    }
  }

  private async generateTestResponse(prompt: string): Promise<string> {
    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Prompt Editor - Aldeia Viva'
        },
        body: JSON.stringify({
          model: this.config.model,
          messages: [{ role: 'user', content: prompt }],
          max_tokens: Math.min(500, this.config.maxTokens),
          temperature: this.config.temperature
        })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.warn('Test response error:', response.status, errorText);
        return `Erro ao gerar resposta de teste (${response.status})`;
      }

      const data = await response.json();
      console.log('Test response data:', data); // Debug log
      
      // Handle different response structures
      let content = '';
      
      if (data.choices && data.choices.length > 0) {
        const choice = data.choices[0];
        
        // Standard content
        if (choice.message?.content) {
          content = choice.message.content;
        }
        // Some models might have different structure
        else if (choice.text) {
          content = choice.text;
        }
        // DeepSeek R1 and reasoning models might have thinking + response
        else if (choice.message?.reasoning) {
          content = choice.message.reasoning + '\n\n' + (choice.message.answer || '');
        }
      }
      
      // If still no content, check for alternative structures
      if (!content && data.response) {
        content = data.response;
      }
      
      if (!content && data.text) {
        content = data.text;
      }
      
      if (!content) {
        console.warn('No content found in response:', JSON.stringify(data, null, 2));
        return 'Sem resposta (estrutura de resposta não reconhecida)';
      }
      
      return content.trim();
    } catch (error) {
      console.error('Test response error:', error);
      return `Erro ao gerar resposta de teste: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }

  private estimateTokens(text: string): number {
    // Simple token estimation based on OpenAI's approximation
    // More accurate than character count / 4
    const words = text.split(/\s+/).filter(word => word.length > 0);
    const characters = text.length;
    
    // Average tokens per word varies, but ~0.75 is a good estimate
    const wordTokens = words.length * 0.75;
    
    // Account for punctuation and special characters
    const punctuationTokens = (characters - text.replace(/[^\w\s]/g, '').length) * 0.3;
    
    return Math.ceil(wordTokens + punctuationTokens);
  }

  private calculateReadability(text: string): number {
    // Simplified readability score based on sentence and word complexity
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const words = text.split(/\s+/).filter(word => word.length > 0);
    
    if (sentences.length === 0 || words.length === 0) return 0;
    
    const averageWordsPerSentence = words.length / sentences.length;
    const averageCharactersPerWord = words.reduce((sum, word) => sum + word.length, 0) / words.length;
    
    // Simple readability formula (inverted complexity score)
    // Lower values = more complex, higher values = more readable
    const complexity = (averageWordsPerSentence * 0.4) + (averageCharactersPerWord * 0.6);
    const readabilityScore = Math.max(0, Math.min(100, 100 - (complexity - 10) * 3));
    
    return Math.round(readabilityScore);
  }

  private createFallbackAnalysis(content: string, language: Language): any {
    // Create a basic analysis when JSON parsing fails
    const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
    
    return {
      analysis: {
        clarity: 70,
        specificity: 65,
        structure: 60,
        context: 55,
        examples: content.toLowerCase().includes('example') || content.toLowerCase().includes('exemplo'),
        constraints: content.toLowerCase().includes('limit') || content.toLowerCase().includes('constraint') || 
                    content.toLowerCase().includes('limite') || content.toLowerCase().includes('restri'),
        overallScore: 63
      },
      suggestions: [
        {
          type: 'clarity',
          severity: 'medium',
          title: language === 'pt-BR' ? 'Análise limitada disponível' : 'Limited analysis available',
          description: language === 'pt-BR' 
            ? 'O modelo não retornou uma resposta estruturada. Considere usar um modelo diferente para análises mais detalhadas.'
            : 'The model did not return a structured response. Consider using a different model for more detailed analysis.',
          example: language === 'pt-BR'
            ? 'Tente usar Claude 3.5 Sonnet ou GPT-4 Turbo para melhores resultados.'
            : 'Try using Claude 3.5 Sonnet or GPT-4 Turbo for better results.',
          reasoning: language === 'pt-BR'
            ? 'Alguns modelos têm dificuldade em seguir formatos estruturados rigorosamente.'
            : 'Some models have difficulty following structured formats rigorously.'
        }
      ]
    };
  }

  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Prompt Editor - Aldeia Viva'
        },
        body: JSON.stringify({
          model: this.config.model,
          messages: [{ role: 'user', content: 'Test connection' }],
          max_tokens: 5,
          temperature: 0.1
        })
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }
}